import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Filter, Calendar, User, Clock, Book, CheckCircle, AlertCircle, Bell, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Course } from "@/types/courseTypes";
import { getAllEnrollments, getCourseById, Enrollment } from "@/utils/firestoreService";
import { doc, deleteDoc, getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BookingsManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"all" | "pending" | "assigned">("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [deleteEnrollmentId, setDeleteEnrollmentId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      try {
        // Fetch all enrollments directly from Firestore
        console.log("Fetching enrollments from Firestore");
        const fetchedEnrollments = await getAllEnrollments();
        console.log("Fetched enrollments:", fetchedEnrollments);
        setEnrollments(fetchedEnrollments);
        
        // Fetch courses
        // For now, we'll keep the course fetching from localStorage until we implement
        // course fetching from Firestore in a separate update
        const storedCourses = localStorage.getItem("courses");
        if (storedCourses) {
          setCourses(JSON.parse(storedCourses));
        }
        
        // Check for new enrollment notifications
        const notifications = JSON.parse(localStorage.getItem("adminNotifications") || "[]");
        const unreadCount = notifications.filter((n: any) => !n.read && n.type === "new-enrollment").length;
        setNotificationCount(unreadCount);
      } catch (error) {
        console.error("Error loading booking data:", error);
        toast({
          title: "Error",
          description: "Failed to load booking data from Firestore",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    // Set up interval to check for new notifications and data updates every 30 seconds
    const updateInterval = setInterval(loadData, 30000);
    
    return () => clearInterval(updateInterval);
  }, [toast]);

  // Filter enrollments based on tab and search term
  const filteredEnrollments = enrollments.filter(enrollment => {
    // Filter by tab
    if (activeTab === "pending" && enrollment.instructorId) {
      return false;
    }
    if (activeTab === "assigned" && !enrollment.instructorId) {
      return false;
    }
    
    // Filter by search term (student name)
    if (searchTerm && enrollment.studentName && !enrollment.studentName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const getStatusBadge = (enrollment: Enrollment) => {
    if (enrollment.instructorId) {
      return <Badge className="bg-green-500">Instructor Assigned</Badge>;
    }
    return <Badge className="bg-amber-500">Pending Assignment</Badge>;
  };

  const handleAssignInstructor = (enrollmentId: string) => {
    navigate(`/admin-dashboard/assign-instructor/${enrollmentId}`);
  };

  const handleDeleteBooking = (enrollmentId: string) => {
    setDeleteEnrollmentId(enrollmentId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteBooking = async () => {
    if (!deleteEnrollmentId) return;
    
    try {
      setIsDeleting(true);
      const db = getFirestore();
      
      // Delete from enrollments collection
      await deleteDoc(doc(db, "enrollments", deleteEnrollmentId));
      
      // Delete related schedules from schedules collection
      const schedulesRef = collection(db, "schedules");
      const schedulesQuery = query(schedulesRef, where("enrollmentId", "==", deleteEnrollmentId));
      const schedulesSnapshot = await getDocs(schedulesQuery);
      
      const deletePromises = schedulesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Update local state
      setEnrollments(enrollments.filter(enrollment => enrollment.id !== deleteEnrollmentId));
      
      toast({
        title: "Success",
        description: "Booking deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast({
        title: "Error",
        description: "Failed to delete booking",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDeleteEnrollmentId(null);
    }
  };

  const markAllNotificationsAsRead = () => {
    const notifications = JSON.parse(localStorage.getItem("adminNotifications") || "[]");
    const updatedNotifications = notifications.map((notification: any) => ({
      ...notification,
      read: true
    }));
    
    localStorage.setItem("adminNotifications", JSON.stringify(updatedNotifications));
    setNotificationCount(0);
    
    toast({
      title: "Notifications cleared",
      description: "All notifications have been marked as read",
    });
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bookings Management</h1>
            <p className="text-gray-600">
              Manage student bookings and instructor assignments
            </p>
          </div>
          
          <div className="relative">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={markAllNotificationsAsRead}
            >
              <Bell className="h-4 w-4" />
              <span>Notifications</span>
              {notificationCount > 0 && (
                <Badge className="bg-red-500 absolute -top-2 -right-2 text-xs w-5 h-5 flex items-center justify-center rounded-full p-0">
                  {notificationCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="all" className="space-y-6" onValueChange={(value) => setActiveTab(value as "all" | "pending" | "assigned")}>
        <TabsList>
          <TabsTrigger value="all">All Bookings</TabsTrigger>
          <TabsTrigger value="pending">Pending Assignment</TabsTrigger>
          <TabsTrigger value="assigned">Assigned</TabsTrigger>
        </TabsList>

        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div className="flex items-center w-full max-w-sm space-x-2">
            <Input
              placeholder="Search by student name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Button variant="outline" size="icon">
              <Search className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Calendar className="h-4 w-4 mr-2" />
              Date Range
            </Button>
          </div>
        </div>

        <TabsContent value="all">
          <BookingsTable 
            enrollments={filteredEnrollments} 
            courses={courses} 
            loading={loading}
            getCourseById={getCourseById}
            getStatusBadge={getStatusBadge}
            handleAssignInstructor={handleAssignInstructor}
            handleDeleteBooking={handleDeleteBooking}
          />
        </TabsContent>

        <TabsContent value="pending">
          <BookingsTable 
            enrollments={filteredEnrollments} 
            courses={courses} 
            loading={loading}
            getCourseById={getCourseById}
            getStatusBadge={getStatusBadge}
            handleAssignInstructor={handleAssignInstructor}
            handleDeleteBooking={handleDeleteBooking}
          />
        </TabsContent>

        <TabsContent value="assigned">
          <BookingsTable 
            enrollments={filteredEnrollments} 
            courses={courses} 
            loading={loading}
            getCourseById={getCourseById}
            getStatusBadge={getStatusBadge}
            handleAssignInstructor={handleAssignInstructor}
            handleDeleteBooking={handleDeleteBooking}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your booking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteBooking} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

interface BookingsTableProps {
  enrollments: Enrollment[];
  courses: Course[];
  loading: boolean;
  getCourseById: (courseId: string) => Promise<Course | null>;
  getStatusBadge: (enrollment: Enrollment) => JSX.Element;
  handleAssignInstructor: (enrollmentId: string) => void;
  handleDeleteBooking: (enrollmentId: string) => void;
}

const BookingsTable = ({
  enrollments,
  courses,
  loading,
  getCourseById,
  getStatusBadge,
  handleAssignInstructor,
  handleDeleteBooking
}: BookingsTableProps) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <p>Loading bookings...</p>
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No bookings found</h3>
        <p className="text-gray-500 mt-2 max-w-md mx-auto">
          There are no bookings matching your current filters.
        </p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle>Booking Details</CardTitle>
        <CardDescription>
          {enrollments.length} booking{enrollments.length !== 1 ? 's' : ''} found
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Booking ID</TableHead>
              <TableHead>Student</TableHead>
              <TableHead>Course</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrollments.map((enrollment) => {
              const courseId = enrollment.courseId || '';
              return (
                <TableRow key={enrollment.id}>
                  <TableCell className="font-mono text-xs">
                    {enrollment.id ? enrollment.id.substring(0, 8) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span>{enrollment.studentName || `Student ${(enrollment.studentId || enrollment.userId || '').substring(0, 8)}`}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{enrollment.courseName || "Unknown Course"}</span>
                      <span className="text-xs text-gray-500">
                        {enrollment.courseType === "theory" ? "Theory" : 
                         enrollment.courseType === "individual" ? "Individual" : 
                         "Practical"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {enrollment.startDate ? (
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                          <span>{format(new Date(enrollment.startDate), "MMM d, yyyy")}</span>
                        </div>
                        {enrollment.preferredTime && (
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <Clock className="h-3 w-3 mr-1" />
                            <span>{enrollment.preferredTime}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">No date specified</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(enrollment)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                    {(enrollment.courseType === "practical" || enrollment.courseType === "individual") && !enrollment.instructorId ? (
                      <Button 
                        size="sm"
                        onClick={() => enrollment.id && handleAssignInstructor(enrollment.id)}
                      >
                        Assign Instructor
                      </Button>
                    ) : enrollment.instructorId ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => enrollment.id && handleAssignInstructor(enrollment.id)}
                      >
                        View Details
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled
                      >
                        Theory Course
                      </Button>
                    )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => enrollment.id && handleDeleteBooking(enrollment.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default BookingsManagement;
